<template>
    <div class="profile-stats">
        <!-- Season picker. Year first, then the season within it — four
             options per year is a shorter list than one flat dropdown that
             grows by four every year.

             Only shown once there's a choice to make: until season rollover
             starts archiving stats, everyone has exactly one season and a
             picker would be furniture. -->
        <div v-if="seasons.length > 1" class="season-picker">
            <b-select v-model="selectedYear" size="is-small" aria-label="Season year">
                <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
            </b-select>
            <b-select v-model="selectedSeasonId" size="is-small" aria-label="Season">
                <option
                    v-for="season in seasonsInSelectedYear"
                    :key="season.id"
                    :value="season.id">{{ season.season }}</option>
            </b-select>
            <span v-if="!isCurrentSeason" class="season-past">past season</span>
        </div>

        <!-- Headline numbers. Six is deliberate: it fills two rows of three
             on desktop and three of two on a phone, so the grid never leaves
             an orphan card on its own line. -->
        <div class="stat-cards">
            <div v-for="card in headlineCards" :key="card.label" class="stat-card glass" :style="overlayStyles">
                <b-icon class="stat-card-icon" :icon="card.icon" />
                <p class="stat-card-value">{{ card.value }}</p>
                <p class="stat-card-label">{{ card.label }}</p>
            </div>
        </div>

        <!-- Per-game breakdown -->
        <section class="stats-panel glass" :style="overlayStyles">
            <header class="stats-panel-header">
                <h2 class="stats-panel-title">Games</h2>
                <!-- The "size = played · number = wins" key that used to sit
                     here is gone. It described two quantities when there is
                     only one — the bubbles encode plays, as both size and
                     label — and it had already been wrong once, still naming
                     plays for size while the code sized by wins. A caption
                     that has to be kept in step with a layout is a caption
                     that will eventually lie; the detail strip under the
                     bubbles says what a game's numbers are by showing them. -->
            </header>

            <game-bubbles :games="breakdown" />

            <p v-if="!isCurrentSeason && !breakdown.length" class="stats-note">
                Nothing played in {{ selectedSeasonName }}.
            </p>
        </section>

        <!-- Achievements -->
        <section class="stats-panel glass" :style="overlayStyles">
            <header class="stats-panel-header">
                <h2 class="stats-panel-title">Achievements</h2>
                <span class="stats-panel-meta">
                    {{ summary.unlocked }} / {{ summary.total }}
                </span>
            </header>

            <div class="achievement-summary">
                <div class="achievement-total-bar">
                    <div
                        class="achievement-total-fill"
                        :style="{ width: `${summary.percent * 100}%`, backgroundColor: accent }"></div>
                </div>
                <div class="rarity-tally">
                    <span
                        v-for="tier in summary.byRarity"
                        :key="tier.rarity"
                        class="rarity-chip"
                        :class="`tier-${tier.rarity}`"
                        :title="`${tier.unlocked} of ${tier.total} ${tier.rarity}`">
                        <achievement-gem class="rarity-chip-gem" :rarity="tier.rarity" />
                        {{ tier.unlocked }}/{{ tier.total }}
                    </span>
                </div>
            </div>

            <div v-if="!catalogLoaded" class="stats-empty">Loading achievements…</div>

            <div v-for="section in sections" :key="section.category" class="achievement-section">
                <h3 class="achievement-category">
                    {{ section.category }}
                    <span class="achievement-category-count">
                        {{ section.unlockedCount }}/{{ section.total }}
                    </span>
                </h3>
                <ul class="achievement-grid">
                    <!-- Locked ones are listed, not hidden. The descriptions
                         are already public from GET /api/achievements, and a
                         page showing only what you have gives no reason to
                         play for the rest. -->
                    <li
                        v-for="achievement in section.achievements"
                        :key="achievement.id"
                        class="achievement-card"
                        :class="[`tier-${achievement.rarity}`, { 'is-locked': !achievement.unlocked }]">
                        <achievement-gem class="achievement-gem" :rarity="achievement.rarity" />
                        <div class="achievement-card-text">
                            <p class="achievement-card-name">{{ achievement.name }}</p>
                            <p class="achievement-card-description">{{ achievement.description }}</p>
                        </div>
                        <b-icon
                            v-if="!achievement.unlocked"
                            class="achievement-card-lock"
                            icon="lock"
                            size="is-small" />
                        <span v-else class="achievement-card-check">✓</span>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import AchievementGem from './AchievementGem.vue'
import GameBubbles from './GameBubbles.vue'
import {
    achievementSections,
    achievementSummary,
    availableSeasons,
    gameBreakdown,
    seasonYears,
    statsForSeason,
    totalGames,
    totalWins,
    unlockedAchievementIds,
    winRate,
} from '../../util/profile.js'

/**
 * The Stats tab: lifetime numbers, a per-game breakdown, and the whole
 * achievement catalog.
 *
 * One tab rather than two. Statistics on their own is half a dozen numbers —
 * not enough to be worth a click — and the achievement list is the thing that
 * gives those numbers somewhere to go. Keeping them together also leaves a
 * slot free for the Servers tab, which is next.
 *
 * Every derivation lives in util/profile.js so it can be tested without
 * mounting anything; this file is the rendering.
 */
export default {
    name: 'ProfileStats',
    components: { AchievementGem, GameBubbles },
    props: {
        // The user this page is about. Passed down rather than read from the
        // store, because the store's copy is always the *signed-in* user —
        // reaching for it directly is what would make this panel show your
        // own numbers under someone else's name.
        dbInfo: { type: Object, required: true },
        // The theme's card treatment and accent, passed down rather than
        // recomputed — the parent owns which theme is applied.
        overlayStyles: { type: Object, default: () => ({}) },
        accent: { type: String, default: '#52D9E9' },
    },
    data() {
        return {
            // null means "the current season", resolved in selectedSeason.
            // Storing the id up front would go stale the moment the season
            // catalog arrives, which it does after this component mounts.
            chosenSeasonId: null,
        }
    },
    computed: {
        currentSeasonId() {
            const season = this.$store.state.profile.season
            return season ? season.id : null
        },

        /** The seasons this user has anything to show for. */
        seasons() {
            return availableSeasons(
                this.dbInfo,
                this.$store.state.profile.seasonCatalog,
                this.currentSeasonId,
            )
        },
        years() { return seasonYears(this.seasons) },

        selectedSeason() {
            const chosen = this.seasons.find(s => s.id === this.chosenSeasonId)
            return chosen || this.seasons[0] || null
        },
        selectedSeasonName() {
            return this.selectedSeason ? this.selectedSeason.name : ''
        },
        isCurrentSeason() {
            return !this.selectedSeason || this.selectedSeason.id === this.currentSeasonId
        },
        seasonsInSelectedYear() {
            const year = this.selectedYear
            return this.seasons.filter(season => season.year === year)
        },

        // The two <select>s. Writing to either resolves to a season id, so
        // the rest of the panel only ever reads `selectedSeason`.
        selectedYear: {
            get() { return this.selectedSeason ? this.selectedSeason.year : null },
            set(year) {
                // Keep the same season within the new year where possible, so
                // stepping through years doesn't also change the season.
                const inYear = this.seasons.filter(season => season.year === year)
                const sameSeason = inYear.find(season =>
                    this.selectedSeason && season.season === this.selectedSeason.season)
                const target = sameSeason || inYear[0]
                if (target) this.chosenSeasonId = target.id
            },
        },
        selectedSeasonId: {
            get() { return this.selectedSeason ? this.selectedSeason.id : null },
            set(id) { this.chosenSeasonId = id },
        },

        /** Per-game stats for whichever season is selected. */
        stats() {
            return statsForSeason(this.dbInfo, this.selectedSeason ? this.selectedSeason.id : null, this.currentSeasonId)
        },
        catalog() { return this.$store.state.profile.achievements },
        catalogLoaded() { return this.catalog.length > 0 },
        achievementsById() { return this.$store.getters['profile/achievementsById'] },

        gameNames() {
            return this.$store.state.profile.games.reduce((map, game) => {
                map[game.id] = game.name
                return map
            }, {})
        },

        breakdown() { return gameBreakdown(this.stats, this.gameNames) },

        headlineCards() {
            const games = totalGames(this.stats)
            const wins = totalWins(this.stats)

            return [
                { label: 'Games played', value: games.toLocaleString(), icon: 'gamepad' },
                { label: 'Wins', value: wins.toLocaleString(), icon: 'trophy' },
                { label: 'Win rate', value: `${Math.round(winRate(wins, games))}%`, icon: 'percent' },
                // Lifetime, unlike the three beside it — XP isn't archived per
                // season, so this number doesn't move with the picker.
                { label: 'Total XP', value: (this.dbInfo.xp || 0).toLocaleString(), icon: 'star' },
            ]
        },

        unlockedIds() {
            return unlockedAchievementIds(this.dbInfo.achievements, this.achievementsById)
        },
        summary() { return achievementSummary(this.catalog, this.unlockedIds) },
        sections() { return achievementSections(this.catalog, this.unlockedIds) },
    },
}
</script>

<style lang="scss" scoped>
.profile-stats {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

// Headline cards

.stat-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;

    @include mobile {
        grid-template-columns: repeat(2, 1fr);
        gap: 0.5rem;
    }
}

.stat-card {
    padding: 1rem;
    // 1rem to match .dashboard-item on the Overview tab. The class itself is
    // deliberately not reused: it is a centred flex row, which would lay the
    // icon/value/label out side by side here and turn every panel below into
    // a shrink-to-fit flex item.
    border-radius: 1rem;
    text-align: center;
}

.stat-card-icon {
    opacity: 0.5;
    margin-bottom: 0.25rem;
}

.stat-card-value {
    font-size: 1.75rem;
    font-weight: bold;
    line-height: 1.1;

    @include mobile { font-size: 1.35rem; }
}

.stat-card-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.7;
}

// Panels

// Season picker

.season-picker {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;

    // Buefy paints the dropdown arrow, the hover border and the focus ring in
    // $primary. Everything else on this tab is greyscale — the sidebar
    // bubbles, the cards, the achievement rows — so a lone cyan chevron reads
    // as something left unstyled rather than as an accent.
    //
    // ::v-deep because these are Buefy's own elements, and the arrow is a
    // pseudo-element on a wrapper this component never writes.
    ::v-deep .select:not(.is-multiple):not(.is-loading) {
        &::after { border-color: $grey-dark; }
        &:hover::after { border-color: $grey-darker; }
    }

    ::v-deep select {
        &:hover { border-color: $grey-light; }

        &:focus,
        &:active,
        &.is-focused {
            border-color: $grey-dark;
            box-shadow: 0 0 0 0.125em rgba(54, 54, 54, 0.15);
        }
    }
}

.season-past {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.6;
    font-weight: bold;
}

.stats-panel {
    padding: 1.25rem;
    border-radius: 1rem;

    @include mobile { padding: 0.85rem; }
}

.stats-panel-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.stats-panel-title {
    font-size: 1.1rem;
    font-weight: bold;
}

.stats-panel-meta {
    font-size: 0.85rem;
    opacity: 0.7;
}

.stats-empty,
.stats-note {
    font-size: 0.85rem;
    opacity: 0.7;
}

.stats-note {
    margin-top: 0.75rem;
    font-size: 0.75rem;
}

// Achievements

.achievement-summary {
    margin-bottom: 1.25rem;
}

.achievement-total-bar {
    height: 0.5rem;
    border-radius: 999px;
    background-color: rgba(127, 127, 127, 0.2);
    overflow: hidden;
    margin-bottom: 0.6rem;
}

.achievement-total-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.3s ease;
}

.rarity-tally {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
}

.rarity-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.75rem;
    font-weight: bold;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background-color: rgba(127, 127, 127, 0.12);
    font-variant-numeric: tabular-nums;
}

.rarity-chip-gem {
    width: 0.85rem;
    height: 0.85rem;
}

.achievement-section + .achievement-section {
    margin-top: 1.25rem;
}

.achievement-category {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: bold;
    opacity: 0.7;
    margin-bottom: 0.5rem;
}

.achievement-category-count {
    font-weight: normal;
    opacity: 0.8;
    margin-left: 0.35rem;
}

.achievement-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
    gap: 0.5rem;
}

.achievement-card {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.65rem;
    border-radius: 8px;
    background-color: rgba(127, 127, 127, 0.1);
    // The tier's colour, as a left edge rather than a fill — a grid of
    // saturated cards is unreadable, and the gem already carries the rarity.
    border-left: 3px solid transparent;

    // `tier-`, not `is-` — variables.scss declares a *global*
    // `.is-common { background: $common }` (and one per tier), which would
    // paint the whole card in that tier's gradient. Scoping raises
    // specificity enough to win on the properties set here, but not on a
    // background this rule never mentions.
    &.tier-common    { border-left-color: $rarity-common; }
    &.tier-rare      { border-left-color: $rarity-rare; }
    &.tier-epic      { border-left-color: $rarity-epic; }
    &.tier-legendary { border-left-color: $rarity-legendary; }
    &.tier-mythical  { border-left-color: $rarity-mythical; }

    // Dimmed, not hidden — still legible enough to read what it takes.
    &.is-locked {
        opacity: 0.45;
        .achievement-gem { filter: grayscale(1); }
    }
}

.achievement-gem {
    flex: 0 0 auto;
    width: 1.5rem;
    height: 1.5rem;
}

.achievement-card-text {
    // Without this the long description pushes the card past its grid track.
    min-width: 0;
    flex: 1 1 auto;
}

.achievement-card-name {
    font-weight: bold;
    font-size: 0.85rem;
}

.achievement-card-description {
    font-size: 0.75rem;
    opacity: 0.75;
}

.achievement-card-check {
    font-weight: bold;
    opacity: 0.7;
}

.achievement-card-lock {
    opacity: 0.6;
}
</style>
